import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { GamificationContext } from "contexts/Gamification";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { Plans } from "schemas/payments";

import { TemplateContentType } from "features/Influencer/ContactList/schema";
import { countTaskByType } from "features/Influencer/HomePage/Gamification/helpers";
import {
  GamificationProfile,
  OrganizationGamificationTask,
} from "features/Influencer/HomePage/Gamification/schema";

import { fetcherAuth } from "./api";
import { makeDeepCopy } from "./updateLocalState";

const _ADDITIONAL_EMAIL_BONUS = 50;

export const useGamification = () => {
  const { setErrorAlert } = useContext(AlertContext);
  const [profileLoading, setProfileLoading] = useState(false);
  const [fetchPitchCountLoading, setFetchPitchCountLoading] = useState(false);
  const { getToken } = useAuth();
  const {
    currentOrg,
    gamificationProfile,
    setGamificationProfile,
    setShowGamificationProfile,
    setCredits,
  } = useContext(OrganizationUserContext);
  const { setSubscription, plans } = useContext(SubscriptionContext);
  const { setPitchCount } = useContext(GamificationContext);
  const { templates } = useContext(OutreachTemplatesContext);

  const fetchGamificationProfile = async (refreshEmailStreak?: boolean) => {
    setProfileLoading(true);
    try {
      const { gamificationProfile, emailStreaks, longestStreak, credits } =
        await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/gamification/profile?refresh=${Boolean(refreshEmailStreak)}`,
        );
      if (!gamificationProfile) {
        setProfileLoading(false);
        return;
      }
      setGamificationProfile(gamificationProfile);
      if (gamificationProfile?.completed) {
        setProfileLoading(false);
        setShowGamificationProfile(false);
        return;
      }
      setCredits(credits);
      if (currentOrg?.isProUser || !currentOrg?.isPaidUser) {
        // Already completed streak , no need to fetch past email streaks
        if (
          gamificationProfile &&
          gamificationProfile?.tasksCompleted &&
          gamificationProfile?.tasksCompleted?.includes(
            OrganizationGamificationTask.email_streak,
          )
        ) {
          setGamificationProfile((prev: GamificationProfile | null) => ({
            ...(prev || gamificationProfile),
            emailStreaks: [],
            longestStreak: 4,
          }));
          setProfileLoading(false);
        } else {
          setGamificationProfile((prev: GamificationProfile | null) => {
            const copy = makeDeepCopy(prev);
            copy.emailStreaks = emailStreaks;
            copy.longestStreak = longestStreak || 0;
            return copy;
          });
        }
      } else {
        setProfileLoading(false);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setProfileLoading(false);
    }
  };

  const refreshGamificationProfile = (refreshEmailStreak?: boolean) => {
    // Only refresh if the profile already existed.
    if (!gamificationProfile) {
      return;
    }
    fetchGamificationProfile(refreshEmailStreak);
    if (!currentOrg?.isPaidUser && refreshEmailStreak) {
      fetchOutreachCount();
    }
  };

  const addTemplateTaskCompleted = (
    contentType: TemplateContentType = TemplateContentType.OUTREACH,
  ) => {
    if (!gamificationProfile) return;
    if (currentOrg?.isVaUser || currentOrg?.isProUser) {
      // Skip gamification on the user's first ever template.
      if (templates.length === 0) return;
      const incomplete =
        countTaskByType(
          gamificationProfile,
          OrganizationGamificationTask.niche_templates,
        ) < (currentOrg?.isProUser ? 1 : 3);
      if (!incomplete) return;

      setGamificationProfile((prev: GamificationProfile | null) => {
        if (!prev) return prev;
        const tasksCompleted = [
          ...(prev.tasksCompleted || []),
          OrganizationGamificationTask.niche_templates,
        ];
        return { ...prev, tasksCompleted };
      });

      if (currentOrg?.isVaUser) {
        setCredits((prev) => [
          ...(prev || []),
          {
            transactionType: OrganizationGamificationTask.niche_templates,
          },
        ]);
      }
    } else {
      const hasOutreach = gamificationProfile?.tasksCompleted?.includes(
        OrganizationGamificationTask.add_template,
      );
      if (
        contentType === TemplateContentType.OUTREACH &&
        hasOutreach &&
        gamificationProfile?.unlockPremiumFeatures
      ) {
        completeTask(OrganizationGamificationTask.niche_templates);
        if (plans) {
          const freeLimit = plans[Plans.FREE]?.monthly_limit;
          setSubscription((prev) => {
            if (!prev || prev.monthlyLimit !== freeLimit) return prev;
            return {
              ...prev,
              monthlyLimit: prev.monthlyLimit + _ADDITIONAL_EMAIL_BONUS,
              remainingMessages:
                (prev.remainingMessages || 0) + _ADDITIONAL_EMAIL_BONUS,
            };
          });
        }
      } else if (contentType === TemplateContentType.OUTREACH) {
        completeTask(OrganizationGamificationTask.add_template);
      } else {
        completeTask(OrganizationGamificationTask.add_followup);
      }
    }
  };

  const fetchCredits = async () => {
    try {
      setProfileLoading(true);
      const { credits } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/credits`,
      );
      setCredits(credits);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setProfileLoading(false);
    }
  };

  const completeTask = (taskCompleted: OrganizationGamificationTask) => {
    if (!gamificationProfile) return;

    setGamificationProfile((prev: GamificationProfile | null) => {
      if (!prev) return prev;
      const existing = prev.tasksCompleted || [];
      if (existing.includes(taskCompleted)) return prev;
      return { ...prev, tasksCompleted: [...existing, taskCompleted] };
    });
  };

  const fetchOutreachCount = async () => {
    if (!currentOrg) return;
    setFetchPitchCountLoading(true);
    try {
      const { count } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/gamification/total-outreach-count`,
      );
      setPitchCount(count);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchPitchCountLoading(false);
    }
  };

  return {
    profileLoading,
    refreshGamificationProfile,
    fetchGamificationProfile,
    addTemplateTaskCompleted,
    fetchCredits,
    completeTask,
    fetchOutreachCount,
    fetchPitchCountLoading,
  };
};
